import React, { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import validate from "validate.js";
import { createOrUpdateSection, hideSectionValidationError, fetchSections } from "actions";
import { isEmpty } from "lodash";
import { F } from "pages/AppraisalManagment/constants/legacyApiKeys";

const schema = {
  title: {
    presence: { allowEmpty: false, message: "^Title is required" },
    length: {
      minimum: 3,
      maximum: 50,
      message: "^Title must be between 3 and 50 characters"
    }
  }
};

const SectionAddModal = ({ show, onHide, mode = "add" }) => {
  const dispatch = useDispatch();
  const sectionState = useSelector((state) => state.sectionState);
  const session = useSelector((state) => state.session);

  // Get object_viewed_id safely
  const object_viewed_id = session?.current_page_permissions?.object_id || session?.current_object_viewed_id || "";

  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";
  const isAddMode = mode === "add";

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      title: "",
      object_viewed_id: object_viewed_id,
    },
    touched: {
      object_viewed_id: true,
    },
    errors: {},
  });

  // Define resetForm function
  const resetForm = React.useCallback(() => {
    setFormState({
      isValid: false,
      values: {
        title: "",
        object_viewed_id: object_viewed_id,
      },
      touched: {
        object_viewed_id: true,
      },
      errors: {},
    });
  }, [object_viewed_id]);

  useEffect(() => {
    if ((isEditMode || isViewMode) && sectionState.sectionRecord && !isEmpty(sectionState.sectionRecord)) {
      setFormState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          id: sectionState.sectionRecord.id,
          title:
            sectionState.sectionRecord.title ||
            sectionState.sectionRecord[F.legacyNameSynonym] ||
            "",
          object_viewed_id: sectionState.sectionRecord.object_viewed_id || object_viewed_id,
        },
        touched: {
          ...prev.touched,
          title: true,
        },
      }));
    }
  }, [sectionState.sectionRecord, isEditMode, isViewMode, object_viewed_id]);

  // Reset form when modal closes or opens in add mode
  useEffect(() => {
    if (show && isAddMode) {
      resetForm();
    }
  }, [show, isAddMode, resetForm]);

  // When validation errors come from server
  useEffect(() => {
    if (!isEmpty(sectionState.validation_error)) {
      setFormState((prev) => ({
        ...prev,
        isValid: false,
        errors: sectionState.validation_error
      }));
    }
  }, [sectionState.validation_error]);

  useEffect(() => {
    if (sectionState.redirectToList) {
      dispatch(fetchSections(object_viewed_id));
      resetForm();
      onHide();
    }
  }, [sectionState.redirectToList, object_viewed_id, dispatch, onHide]);

  // Validate client-side whenever values change
  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState((prev) => ({
      ...prev,
      isValid: !errors,
      errors: errors || {}
    }));
  }, [formState.values]);

  const handleChange = (e) => {
    e.persist();
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [e.target.name]: e.target.value
      },
      touched: {
        ...prev.touched,
        [e.target.name]: true
      }
    }));
    dispatch(hideSectionValidationError(e.target.name));
  };

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (isViewMode) {
      // In view mode, just close the modal
      onHide();
      return;
    }

    try {
      const method = isEditMode ? "PUT" : "POST";
      dispatch(createOrUpdateSection(formState.values, method));
    } catch (error) {
      console.error('Error submitting section:', error);
    }
  };


  const hasError = (field) =>
    formState.touched[field] && formState.errors[field] ? true : false;

  const handleCancel = () => {
    resetForm();
    onHide();
  };

  const getModalTitle = () => {
    if (isViewMode) return "View Section";
    if (isEditMode) return "Edit Section";
    return "Add Section";
  };

  return (
    <Modal show={show} onHide={handleCancel} centered size="lg" className="add-section-modal">
      <div>
        <h1 className="font-20 font-weight-s-bold heading-color text-center border-bottom mb-0 pb-3">
          {getModalTitle()}
        </h1>
        <div className="mt-3">
          <Form.Group className="mb-3">
            <Form.Label className="label-input">Title</Form.Label>
            <Form.Control
              type="text"
              name="title"
              placeholder="Enter Section Title"
              value={formState.values.title || ""}
              onChange={handleChange}
              readOnly={isViewMode}
              disabled={isViewMode}
              className={`input-field ${hasError("title") ? "is-invalid" : ""} ${isViewMode ? "bg-light" : ""}`}
            />
            {hasError("title") && (
              <div className="invalid-feedback">
                {formState.errors.title[0]}
              </div>
            )}
          </Form.Group>
        </div>
      </div>
      <div>
        <button type="button" className="c-b-btn" onClick={handleCancel}>
          {isViewMode ? "Close" : "Cancel"}
        </button>
        {!isViewMode && (
          <button
            type="button"
            className="n-u-btn"
            onClick={handleSubmit}
            disabled={!formState.isValid}
          >
            Save
          </button>
        )}
      </div>
    </Modal>
  );
};

export default SectionAddModal;
